/**
 * Auth store -- Zustand with localStorage persistence.
 *
 * Manages: access_token, refresh_token, user object, isAuthenticated.
 * Hydrates from localStorage on app startup.
 * Provides login/logout/refresh actions.
 */

import { create } from 'zustand';
import type { AuthUser } from '@/services/authService';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY_ACCESS = 'opus-ia-token';
const STORAGE_KEY_REFRESH = 'opus-ia-refresh-token';
const STORAGE_KEY_USER = 'opus-ia-user';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isHydrated: boolean;

  /** Store tokens and user after successful login */
  setAuth: (user: AuthUser, accessToken: string, refreshToken: string) => void;

  /** Update tokens after refresh (user stays the same) */
  setTokens: (accessToken: string, refreshToken: string) => void;

  /** Merge fresh user fields (e.g. from GET /v1/me) into the stored user and persist them */
  updateUser: (patch: Partial<AuthUser>) => void;

  /** Clear all auth state and localStorage */
  clearAuth: () => void;

  /** Set loading state */
  setLoading: (loading: boolean) => void;

  /** Hydrate state from localStorage on startup */
  hydrate: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // localStorage full or blocked -- fail silently
  }
}

function safeRemoveItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // fail silently
  }
}

function persistToStorage(
  user: AuthUser,
  accessToken: string,
  refreshToken: string,
): void {
  safeSetItem(STORAGE_KEY_ACCESS, accessToken);
  safeSetItem(STORAGE_KEY_REFRESH, refreshToken);
  safeSetItem(STORAGE_KEY_USER, JSON.stringify(user));
}

function clearStorage(): void {
  safeRemoveItem(STORAGE_KEY_ACCESS);
  safeRemoveItem(STORAGE_KEY_REFRESH);
  safeRemoveItem(STORAGE_KEY_USER);
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: true,
  isHydrated: false,

  setAuth: (user, accessToken, refreshToken) => {
    persistToStorage(user, accessToken, refreshToken);
    set({
      user,
      accessToken,
      refreshToken,
      isAuthenticated: true,
      isLoading: false,
    });
  },

  setTokens: (accessToken, refreshToken) => {
    safeSetItem(STORAGE_KEY_ACCESS, accessToken);
    safeSetItem(STORAGE_KEY_REFRESH, refreshToken);
    set({ accessToken, refreshToken });
  },

  updateUser: (patch) =>
    set((state) => {
      if (!state.user) return state;
      const user = { ...state.user, ...patch };
      safeSetItem(STORAGE_KEY_USER, JSON.stringify(user));
      return { user };
    }),

  clearAuth: () => {
    clearStorage();
    set({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
    });
  },

  setLoading: (isLoading) => set({ isLoading }),

  hydrate: () => {
    const accessToken = safeGetItem(STORAGE_KEY_ACCESS);
    const refreshToken = safeGetItem(STORAGE_KEY_REFRESH);
    const userJson = safeGetItem(STORAGE_KEY_USER);

    if (accessToken && refreshToken && userJson) {
      try {
        const user = JSON.parse(userJson) as AuthUser;
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
          isHydrated: true,
        });
        return;
      } catch {
        // corrupted data -- clear and fall through
        clearStorage();
      }
    }

    set({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      isHydrated: true,
    });
  },
}));
